import { Injectable, inject } from "@angular/core";
import { environment } from "../../../environments/environment";
import { MsalV3Service } from "./msal-V3.service";

// Loaded via angular.json scripts — exposes window.EVAChatbotWebComponent
declare const EVAChatbotWebComponent: {
  createSimpleBackendAdapter: (options: any) => any;
};

@Injectable({ providedIn: "root" })
export class EvaChatbotService {
  private authService = inject(MsalV3Service);

  createAdapter() {
    return EVAChatbotWebComponent.createSimpleBackendAdapter({
      baseUrl: environment.eva.apiBaseUrl,
      // Return the full auth headers (Bearer + Subscription Key)
      // instead of just the token — matches InternalWebApp's adapter pattern
      getAuthToken: () => this.authService.getAccessToken(),
      userId: this.authService.getUserId(),
      userEmail: this.authService.getUserEmail(),
      userName: this.authService.getUserName(),
    });
  }

  /**
   * Full chatbot config matching InternalWebApp's chatbot/config.ts
   */
  createConfig() {
    return {
      api: {
        baseUrl: environment.eva.apiBaseUrl,
        timeout: 30000,
        retryAttempts: 3,
        retryDelay: 1000,
        authentication: "api-key" as const,
        headers: {
          "X-Client-Version": "1.0.0",
        },
        endpoints: {
          chat: {
            sendMessage: "/eva_snow_ai_engine",
            streamMessage: "/eva_snow_ai_engine",
            getConversationHistory: "/sessions/:id/conversations",
            createConversation: "/sessions",
            updateConversation: "/sessions/:id",
            deleteConversation: "/sessions/:id",
          },
          feedback: {
            sendMessageFeedback:
              "/sessions/:sessionId/conversations/:conversationId/feedback",
            sendConversationFeedback: "/sessions/:id/rating",
          },
          context: {
            getUserContext: "/user/context",
            getAccountInfo: "/user/account",
          },
        },
      },

      chat: {
        streaming: true,
        maxMessageLength: 2000,
        maxUploadSize: 10 * 1024 * 1024,
        allowedFileTypes: ["pdf", "docx", "txt", "jpg", "png"],
        typingIndicatorDelay: 500,
        autoSaveInterval: 5000,
        showConversationHistory: true,
        showPromptSuggestions: true,
        enableFileUpload: false,
        enableMessageEditing: false,
        enableMessageDeletion: false,
      },

      ui: {
        theme: "light" as const,
        locale: "en-US",
        headerTitle: "EVA - Support Assistant",
        inputPlaceholder: "Type your message here...",
        position: "right" as const,
        size: "medium" as const,
        cssVariables: {
          "--eva-primary": "#1d4ed8",
          "--eva-secondary": "#f0f0f0",
          "--eva-text": "#333333",
          "--eva-border-radius": "8px",
        },
      },

      rendering: {
        enableCodeBlocks: true,
        enableMarkdown: true,
        enableLinks: true,
        enableImages: false,
        enableTables: true,
      },

      liveAgent: {
        enabled: true,
        startEndpoint: "/live_agent",
        negotiateEndpoint: "/live_agent/negotiate",
        enableSignalR: true,
        enableNaturalLanguageDetection: true,
        connectionTimeout: 70000,
        showWaitingMessage: true,
        waitingMessage:
          "Connecting you to a live agent... This may take up to 60 seconds.",
      },
    };
  }
}
